import { useEffect, useState } from 'react';
import { currentVoice, listHindiVoices, setChosenVoice, useChosenVoiceKey, voiceKey } from '../lib/voice';
import { Sheet } from './ui';

/** Some phones list two voices under the same display name — one for Devanagari text, one for Latin script. */
const isLatinScript = (lang: string) => /[-_#]latn\b/i.test(lang);

/** Speaks "गुआ खाया" with one specific voice, bypassing the normal picking logic. */
function testVoice(voice: SpeechSynthesisVoice) {
  speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance('ज़ाकिर ने गुआ खाया है');
  u.voice = voice;
  u.lang = voice.lang;
  u.rate = 0.95;
  speechSynthesis.speak(u);
}

/**
 * Lets the person hear and choose which of the device's Hindi voices to use.
 * Auto-picking the "best" voice has gone wrong before — including a change
 * that was meant to fix a mispronunciation but may have made it worse on some
 * phones — and there is no way to test every Android device's voice list from
 * here, so a direct choice the person can hear for themselves is the reliable
 * fix instead of another guess.
 */
export function VoicePicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const chosenKey = useChosenVoiceKey();
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    if (!open) return;
    const refresh = () => setVoices(listHindiVoices());
    refresh();
    speechSynthesis.addEventListener?.('voiceschanged', refresh);
    return () => speechSynthesis.removeEventListener?.('voiceschanged', refresh);
  }, [open]);

  const active = currentVoice();

  return (
    <Sheet open={open} onClose={onClose} title="Choose a Hindi voice">
      <p className="mb-4 text-sm text-ink-soft">
        Tap 🔊 to hear each voice say "गुआ खाया" (Guaa khaya), then pick the one that sounds right. Some phones have more
        than one Hindi voice, and the wrong one can read it badly.
      </p>
      {voices.length === 0 ? (
        <p className="py-6 text-center text-ink-soft">
          No Hindi voice was found on this device. Add one under your phone's text-to-speech settings, then reopen this.
        </p>
      ) : (
        <ul className="grid gap-2">
          {voices.map((v) => {
            const key = voiceKey(v);
            const isChosen = chosenKey ? key === chosenKey : v === active;
            const latin = isLatinScript(v.lang);
            return (
              <li key={key} className="flex items-center gap-2">
                <button
                  type="button"
                  aria-pressed={isChosen}
                  onClick={() => setChosenVoice(key)}
                  className={`flex min-h-12 flex-1 items-center justify-between gap-2 rounded-xl px-3 py-2 text-left ring-1 transition ${
                    isChosen ? 'bg-success-700/10 ring-success-600' : 'ring-ink/15 hover:bg-ink/5'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate font-semibold">{v.name}</span>
                      {latin && (
                        <span className="shrink-0 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-bold text-amber-950">
                          Latin script
                        </span>
                      )}
                    </span>
                    <span className="block text-xs text-ink-soft">
                      {v.lang}
                      {v.localService ? ' · on this device' : ' · needs a connection'}
                      {latin && ' · reads Roman spelling, not गुआ'}
                    </span>
                  </span>
                  {isChosen && <span className="shrink-0 font-bold text-success-700">✓</span>}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-ink min-h-12 px-3 text-sm"
                  onClick={() => testVoice(v)}
                  aria-label={`Hear ${v.name}, ${v.lang}, say Guaa khaya`}
                >
                  🔊 Test
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {chosenKey && (
        <button type="button" className="btn btn-ghost mt-3 w-full text-sm" onClick={() => setChosenVoice(null)}>
          Reset to automatic choice
        </button>
      )}
    </Sheet>
  );
}
